import { BACKEND_API_VERSION, getAuthToken } from '@/shared/api/axios.instance';
import type { TrackingEventDto } from '@/shared/api/modules/tracking';
import { ENV } from '@/shared/lib/env';

export type { TrackingEventDto as TrackingEvent } from '@/shared/api/modules/tracking';

const FLUSH_INTERVAL_MS = 10_000;
const MAX_QUEUE_SIZE = 20;

/**
 * Per-tab session id, persisted in `sessionStorage` so it survives a reload
 * of the same tab but never leaks across tabs (each tab gets its own entry).
 */
const SESSION_STORAGE_KEY = 'insightlanding:tracking:tab-session-id';

let cachedSessionId: string | null = null;
let queue: TrackingEventDto[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;

export const getTrackingSessionId = (): string => {
  if (cachedSessionId) return cachedSessionId;
  if (typeof window === 'undefined') return '';

  const existing = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
  if (existing) {
    cachedSessionId = existing;
    return existing;
  }

  const created = crypto.randomUUID();
  window.sessionStorage.setItem(SESSION_STORAGE_KEY, created);
  cachedSessionId = created;
  return created;
};

const getTrackingEventsUrl = () =>
  `${ENV.backendApiUrl}/${BACKEND_API_VERSION}/tracking/events`;

/**
 * Sends a batch with `fetch`, never axios: `keepalive` is what lets the
 * request survive a tab close / navigation, and axios does not expose it.
 *
 * `navigator.sendBeacon` was considered for the unload/hidden path (it is
 * more reliably delivered than a keepalive fetch on some browsers) but it
 * cannot set an `Authorization` header, and this endpoint requires a bearer
 * token. Rather than send an unauthenticated beacon that the backend would
 * reject anyway, every flush path — including unload/hidden — uses this
 * same authenticated `fetch(..., { keepalive: true })` call and accepts
 * best-effort delivery.
 */
const sendBatch = (events: TrackingEventDto[], keepalive: boolean) => {
  if (events.length === 0) return;

  const token = getAuthToken();
  if (!token) return;

  fetch(getTrackingEventsUrl(), {
    method: 'POST',
    keepalive,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ events }),
  }).catch(() => {
    // Best-effort: tracking must never surface an error to the user or
    // block navigation/unload.
  });
};

export const enqueueTrackingEvent = (event: TrackingEventDto) => {
  queue.push(event);

  if (queue.length >= MAX_QUEUE_SIZE) {
    flushTrackingQueue();
  }
};

export const flushTrackingQueue = (keepalive = false) => {
  if (queue.length === 0) return;

  const events = queue;
  queue = [];
  sendBatch(events, keepalive);
};

export const startTrackingFlushLoop = () => {
  if (flushTimer) return;
  flushTimer = setInterval(() => flushTrackingQueue(false), FLUSH_INTERVAL_MS);
};

export const stopTrackingFlushLoop = () => {
  if (flushTimer) {
    clearInterval(flushTimer);
    flushTimer = null;
  }
};
